import { Dispatch, SetStateAction, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import { useAppDispatch } from "@/redux/hooks";
import { ERROR_TYPE, getErrorMessage, translateRequestError } from "@/utils/errors";
import { clearGlobalSearch } from "@/pages/Private/redux/search/search.slice";
import { useLazyGetContactProspectDetailQuery } from "@/pages/Private/redux/contact/contact.api";
import { useLazyGetCompanyEnrichPersonQuery } from "@/pages/Private/redux/companyEnrich/companyEnrich.api";
import { ListPersonGlobalSearchInfoDTO } from "@/pages/Private/redux/search/search.schema";

import { SelectedLinkedInItem } from "./singleSelect";
import { resolveContactLinkedinUrl, resolveLinkedInItemUrl } from "./resolveSelectionLinkedinUrl";

interface UseConfirmSelectionArgs {
	selectedContactIds: number[];
	setSelectedContactIds: Dispatch<SetStateAction<number[]>>;
	selectedLinkedInItems: SelectedLinkedInItem[];
	setSelectedLinkedInItems: Dispatch<SetStateAction<SelectedLinkedInItem[]>>;
	// The global-search slice's contacts bucket — used to look up a selected contact's linkedinUrl.
	searchContacts: ListPersonGlobalSearchInfoDTO[];
	onConfirmSelection?: (linkedinUrls: string[]) => void | Promise<void>;
	setIsOpened: Dispatch<SetStateAction<boolean>>;
	// The modal's snackbar opener — useSnackBar is per-instance, and only the modal's instance is
	// actually rendered, so the hook must report errors through it rather than its own.
	handleOpenSnackbar: (text: string) => void;
}

interface UseConfirmSelectionResult {
	isConfirming: boolean;
	handleConfirmSelection: () => Promise<void>;
}

/**
 * Encapsulates the "confirm selection" mode of GlobalSearchModal: every selected person is
 * resolved to a LinkedIn URL and the URLs are handed to the caller's `onConfirmSelection`
 * instead of being added to a list. Used by flows that run their own mutation on the result —
 * replacing a dummy prospect on a ListPerson, adding people to a tracker organization.
 */
export function useConfirmSelection({
	selectedContactIds,
	setSelectedContactIds,
	selectedLinkedInItems,
	setSelectedLinkedInItems,
	searchContacts,
	onConfirmSelection,
	setIsOpened,
	handleOpenSnackbar,
}: UseConfirmSelectionArgs): UseConfirmSelectionResult {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();

	const [triggerGetContactProspectDetail] = useLazyGetContactProspectDetailQuery();
	const [resolveCompanyEnrichPerson] = useLazyGetCompanyEnrichPersonQuery();

	const [isConfirming, setIsConfirming] = useState(false);

	const handleConfirmSelection = useCallback(async () => {
		if (!onConfirmSelection) {
			return;
		}

		setIsConfirming(true);

		try {
			const resolved = await Promise.all([
				...selectedContactIds.map((id) =>
					resolveContactLinkedinUrl(id, searchContacts, (contactId) =>
						triggerGetContactProspectDetail(contactId)
							.unwrap()
							.catch(() => undefined)
					)
				),
				...selectedLinkedInItems.map((li) =>
					resolveLinkedInItemUrl(li, (companyEnrichId) =>
						resolveCompanyEnrichPerson(companyEnrichId)
							.unwrap()
							.catch(() => undefined)
					)
				),
			]);

			const linkedinUrls = resolved.filter((url): url is string => !!url);

			// Nothing resolvable — report and keep the modal open so the user can adjust.
			if (linkedinUrls.length === 0) {
				handleOpenSnackbar(t("basics.error"));

				return;
			}

			await onConfirmSelection(linkedinUrls);

			setSelectedContactIds([]);
			setSelectedLinkedInItems([]);
			dispatch(clearGlobalSearch());
			setIsOpened(false);
		} catch (error) {
			// The caller's mutation failed — keep the modal (and selection) open for a retry. A refusal
			// the API explains, such as a 402 for credits, says why (PREC-4438).
			const explained =
				getErrorMessage((error ?? {}) as FetchBaseQueryError) !== ERROR_TYPE.SOMETHING_WRONG;

			handleOpenSnackbar(explained ? translateRequestError(error) : t("basics.error"));
		} finally {
			setIsConfirming(false);
		}
	}, [
		onConfirmSelection,
		selectedContactIds,
		selectedLinkedInItems,
		searchContacts,
		triggerGetContactProspectDetail,
		resolveCompanyEnrichPerson,
		handleOpenSnackbar,
		t,
		dispatch,
		setIsOpened,
		setSelectedContactIds,
		setSelectedLinkedInItems,
	]);

	return { isConfirming, handleConfirmSelection };
}
